import { type RowData, type SortDirection, Subscribe } from "@tanstack/react-table";
import { ChevronsUpDown, EyeOff } from "lucide-react";
import React from "react";
import { ChevronDown, ChevronUp, Ellipsis, Filter } from "../../Icons";
import { Badge } from "../Badge";
import { Button } from "../Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../DropdownMenu";
import type { HeaderContext } from "./features";
import { FilterInput } from "./FilterInput";

const sortIconLookup = {
  desc: <ChevronDown />,
  asc: <ChevronUp />,
  false: <ChevronsUpDown />,
};

const getIcon = (params: { canSort: boolean; isSorted: false | SortDirection; canFilter: boolean }) => {
  if (params.canSort) {
    return sortIconLookup[`${params.isSorted}`];
  }
  if (params.canFilter) {
    return <Filter />;
  }
  return <Ellipsis />;
};

export function DataTableColumnHeader<TData extends RowData, TValue>({
  column,
}: Readonly<HeaderContext<TData, TValue>>) {
  return (
    <Subscribe source={column.table.store} selector={(state) => [state.sorting, state.columnFilters]}>
      {() => (
        <ColumnMenu
          column={column}
          isSorted={column.getIsSorted()}
          filterValue={column.getFilterValue()}
          isFiltered={column.getIsFiltered()}
        />
      )}
    </Subscribe>
  );
}

interface ColumnMenuProps<TData extends RowData, TValue> {
  column: HeaderContext<TData, TValue>["column"];
  isSorted: false | SortDirection;
  filterValue: unknown;
  isFiltered: boolean;
}

function ColumnMenu<TData extends RowData, TValue>({
  column,
  isSorted,
  filterValue,
  isFiltered,
}: Readonly<ColumnMenuProps<TData, TValue>>) {
  const canSort = column.getCanSort();
  const canFilter = column.getCanFilter();
  const canHide = column.accessorFn !== undefined && column.getCanHide();
  if (!canSort && !canFilter && !canHide) {
    return null;
  }

  let items = [];
  if (canFilter && column.columnDef.meta?.filter) {
    items.push(
      <div key="filter" role="presentation" onKeyDown={(e) => e.stopPropagation()}>
        <FilterInput
          name={typeof column.columnDef.header === "string" ? column.columnDef.header : column.id}
          filter={column.columnDef.meta.filter}
          value={filterValue}
          onValueChange={(value) => column.setFilterValue(value)}
        />
      </div>,
    );
  }
  if (canSort) {
    items.push(
      <React.Fragment key="sort">
        <DropdownMenuItem
          onClick={() => (isSorted === "asc" ? column.clearSorting() : column.toggleSorting(false))}
          aria-selected={isSorted === "asc"}
          className={isSorted === "asc" ? "bg-brand/15" : ""}
        >
          <ChevronUp className="size-3" />
          Asc
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => (isSorted === "desc" ? column.clearSorting() : column.toggleSorting(true))}
          aria-selected={isSorted === "desc"}
          className={isSorted === "desc" ? "bg-brand/15" : ""}
        >
          <ChevronDown className="size-3" />
          Desc
        </DropdownMenuItem>
      </React.Fragment>,
    );
  }
  if (canHide) {
    items.push(
      <DropdownMenuItem key="hide" onClick={() => column.toggleVisibility(false)}>
        <EyeOff className="size-3" />
        Hide
      </DropdownMenuItem>,
    );
  }
  // eslint-disable-next-line react/no-array-index-key
  items = items.flatMap((item, index) => [item, index < items.length - 1 && <DropdownMenuSeparator key={index} />]);

  return (
    <DropdownMenu>
      <Badge badgeContent={isFiltered} badgeClassName="top-1 right-1 h-2 min-w-2">
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="small" className="px-1 text-small data-[state=open]:bg-brand/15">
            {getIcon({ canSort, isSorted, canFilter })}
          </Button>
        </DropdownMenuTrigger>
      </Badge>
      <DropdownMenuPortal>
        <DropdownMenuContent align="center" side="bottom" className="w-60">
          {items}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}
